import React from "react";

import { trpc } from "@blis/trpc/react";

import { useFeatureFlags, useSettings } from "@/features/admin/hooks";
import {
  printInoculationLabels,
  printInoculationLabelsWithTemplate,
  printSpecimenLabelsWithLayout,
  printSpecimenLabelsWithTemplate,
} from "./index";
import { getLayoutForDPI } from "./layouts";
import type { LabelAccession, LabelSample } from "./types";
import { useElectronPrinter } from "./useElectronPrinter";

export type LabelType = "specimen" | "archive" | "material";

/**
 * Hook for printing labels with automatic DPI setting and layout selection
 */
export function usePrintLabel() {
  useElectronPrinter();
  const { settings } = useSettings();
  const { featureFlags } = useFeatureFlags();
  const apiUtils = trpc.useUtils();

  const dpiSetting = React.useMemo(() => {
    const dpiSetting = settings.printerDpi;
    const dpi = dpiSetting ? Number(dpiSetting) : 203;

    if (Number.isNaN(dpi) || (dpi !== 203 && dpi !== 300)) {
      console.warn(
        `Invalid DPI setting: ${dpiSetting}, falling back to 203 DPI`,
      );
      return 203;
    }

    return dpi;
  }, [settings.printerDpi]);

  const hardwareOffsets = React.useMemo(() => {
    const parsedLeft = settings.printerOffsetLeftMm
      ? Number(settings.printerOffsetLeftMm)
      : 0;
    const parsedTop = settings.printerOffsetTopMm
      ? Number(settings.printerOffsetTopMm)
      : 0;

    const leftMm = Number.isNaN(parsedLeft) ? 0 : parsedLeft;
    const topMm = Number.isNaN(parsedTop) ? 0 : parsedTop;

    return {
      leftMm,
      topMm,
    };
  }, [settings.printerOffsetLeftMm, settings.printerOffsetTopMm]);

  const printQuality = React.useMemo(() => {
    const parseOptionalNumber = (raw: string | null | undefined) => {
      if (raw === undefined || raw === null || raw === "") {
        return undefined;
      }
      const parsed = Number(raw);
      return Number.isNaN(parsed) ? undefined : parsed;
    };

    return {
      printSpeed: parseOptionalNumber(settings.printerPrintSpeed),
      darkness: parseOptionalNumber(settings.printerDarkness),
    };
  }, [settings.printerPrintSpeed, settings.printerDarkness]);

  const labelPrintOptions = React.useMemo(
    () => ({
      ...hardwareOffsets,
      ...printQuality,
    }),
    [hardwareOffsets, printQuality],
  );

  const getTemplate = React.useCallback(
    async (labelType: LabelType) => {
      // React Query owns the cache (cleared on page refresh). Templates rarely
      // change, so keep them fresh for an hour within a session.
      const res = await apiUtils.labels.template.fetch(
        {
          labelType,
          dpi: dpiSetting,
        },
        { staleTime: 1000 * 60 * 60 },
      );
      return res.template;
    },
    [apiUtils, dpiSetting],
  );

  React.useEffect(() => {
    const labelTypes: LabelType[] = ["specimen", "archive", "material"];
    void Promise.all(
      labelTypes.map((labelType) => getTemplate(labelType)),
    ).catch((error) => {
      console.warn("Failed to preload label templates", error);
    });
  }, [getTemplate]);

  const printSpecimenLabels = React.useCallback(
    async (accession: LabelAccession, samples: LabelSample[]) => {
      if (featureFlags?.enableLabelsV2) {
        const template = await getTemplate("specimen");
        await printSpecimenLabelsWithTemplate(
          accession,
          samples,
          template,
          "specimen",
          dpiSetting,
          labelPrintOptions,
        );
        return;
      }

      await printSpecimenLabelsWithLayout(
        accession,
        samples,
        undefined,
        dpiSetting,
      );
    },
    [dpiSetting, featureFlags?.enableLabelsV2, getTemplate, labelPrintOptions],
  );

  const printArchiveLabels = React.useCallback(
    async (accession: LabelAccession, samples: LabelSample[]) => {
      if (featureFlags?.enableLabelsV2) {
        const template = await getTemplate("specimen");
        await printSpecimenLabelsWithTemplate(
          accession,
          samples,
          template,
          "archive",
          dpiSetting,
          labelPrintOptions,
        );
        return;
      }

      const archiveLayout = getLayoutForDPI("archive", dpiSetting);
      await printSpecimenLabelsWithLayout(
        accession,
        samples,
        archiveLayout,
        dpiSetting,
      );
    },
    [dpiSetting, featureFlags?.enableLabelsV2, getTemplate, labelPrintOptions],
  );

  const printMaterialLabels = React.useCallback(
    async (
      samples: Parameters<typeof printInoculationLabels>[0],
      materials: Parameters<typeof printInoculationLabels>[1],
    ) => {
      if (featureFlags?.enableLabelsV2) {
        const template = await getTemplate("material");
        await printInoculationLabelsWithTemplate(
          samples,
          materials,
          template,
          dpiSetting,
          labelPrintOptions,
        );
        return;
      }

      await printInoculationLabels(samples, materials, dpiSetting);
    },
    [dpiSetting, featureFlags?.enableLabelsV2, getTemplate, labelPrintOptions],
  );

  return {
    printSpecimenLabels,
    printArchiveLabels,
    printMaterialLabels,
  };
}
